import { CaseInsensitiveMap } from "Global/Base/DataTypes/CaseInsensitiveMap";
import { IRenderer, IRendererRegistrator } from "../types";

/**
 * Používá se pro uchování priorit k daným rendererům (_renderersPriorities).
 */
type PriorityRegisterItem = {
    /** Priorita rendereru. */
    readonly priority: number;
    /** Klíč rendereru. */
    readonly itemKey: string;
};

/**
 * Registrátor, který umožnuje jednotlivým rendererům přidat priority.
 *
 * Priorit se využívá při hledání, který renderer může zobrazit daný element (v metodě `findRenderer`).
 * Vracíme renderer s největší prioritou, který splňuje podmínku `canRender`. Pokud žádný ze zaregistrovaných rendererů
 * neumí element zobrazit, použije se defaultní renderer.
 */
export abstract class PriorityRegistrator<RendererType extends IRenderer = IRenderer> implements IRendererRegistrator<RendererType> {
    protected _defaultRenderer: RendererType;

    /** Mapa registrovaných rendererů. */
    protected _registeredRenderers: CaseInsensitiveMap<RendererType> = new CaseInsensitiveMap<RendererType>();
    /** Pole registrovaných rendererů a jejich priorit seřazené sestupně. */
    protected _renderersPriorities: PriorityRegisterItem[] = [];

    constructor(defaultRenderer: RendererType) {
        this._defaultRenderer = defaultRenderer;
        this._registeredRenderers.set(defaultRenderer.key, defaultRenderer);
    }

    public registerFactory(_rendererFactory: () => RendererType, _priority: number): void {
        throw new Error("Method not implemented.");
    }

    public register(renderer: RendererType, priority: number = 1): void {
        if (!this._registeredRenderers.has(renderer.key)) {
            this._registeredRenderers.set(renderer.key, renderer);
            this._renderersPriorities.push({ priority, itemKey: renderer.key });
            this._renderersPriorities.sort((a, b) => b.priority - a.priority); // Seřadí pole sestupně podle priority.
        } else {
            throw new Error(`Renderer with renderer.key="${renderer.key}" is already registred.`);
        }
    }

    public getRendererByKey(key: string): RendererType {
        if (this._registeredRenderers.has(key)) {
            return this._registeredRenderers.get(key) ?? this._defaultRenderer;
        }
        return this._defaultRenderer;
        // Defaultní hodnotu vracím protože TS nedokáže odvodit z mapy, že tento případ je odchycen v předchozím kroku.
    }

    /** Metoda, která prochází renderery podle priority a vrácí první renderer, který bude odpovídat podmínce.*/
    public abstract findRenderer(...args: any): RendererType;

    /**
     * Vrací všechny zaregistrované renderery seřazené podle priority (renderer s nejvyšší prioritou na začátku).
     */
    public getAll(): RendererType[] {
        return [
            ...this._renderersPriorities.map(({ itemKey }) => this.getRendererByKey(itemKey)),
            this._defaultRenderer,
        ];
    }
}
